import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { 
  Award, 
  FileCheck, 
  Clock,
  CreditCard,
  Loader2,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberDashboard() {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await axios.get(`${API}/member/dashboard`, getAuthHeader());
        setDashboardData(response.data);
      } catch (error) {
        console.error('Error fetching dashboard:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const handlePayment = async () => {
    if (!dashboardData?.application?.id) return;
    
    setPaymentLoading(true);
    try {
      const response = await axios.post(
        `${API}/payments/create-session?application_id=${dashboardData.application.id}`,
        {},
        {
          ...getAuthHeader(),
          headers: {
            ...getAuthHeader().headers,
            'Origin': window.location.origin
          }
        }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success('Membership activated!');
        window.location.reload();
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-[#F5A623] animate-spin" />
      </div>
    );
  }

  const { certificate, application } = dashboardData || {};
  const showPaymentPrompt = application?.status === 'approved' && !certificate;

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0E285B] mb-8">
        Welcome, {user?.full_name}
      </h1>

      {/* Status Cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* Application Status */}
        <div className="bg-white p-6 rounded-sm border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <FileCheck className="h-6 w-6 text-[#F5A623]" />
            <h3 className="font-semibold text-[#0E285B]">Application Status</h3>
          </div>
          {application ? (
            <div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                application.status === 'active' ? 'badge-active' :
                application.status === 'approved' ? 'badge-approved' :
                application.status === 'pending' ? 'badge-pending' :
                'badge-rejected'
              }`}>
                {application.status?.charAt(0).toUpperCase() + application.status?.slice(1)}
              </span>
              <p className="text-slate-600 text-sm mt-2">
                Membership Type: <span className="font-medium capitalize">{application.membership_type}</span>
              </p>
            </div>
          ) : (
            <p className="text-slate-500">No application found</p>
          )}
        </div>

        {/* Membership Status */}
        <div className="bg-white p-6 rounded-sm border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <Award className="h-6 w-6 text-[#F5A623]" />
            <h3 className="font-semibold text-[#0E285B]">Membership Status</h3>
          </div>
          {certificate ? (
            <div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium badge-active">
                Active Member
              </span>
              <p className="text-slate-600 text-sm mt-2">
                Certificate: <span className="font-mono">{certificate.certificate_number}</span>
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-slate-500">
              <Clock className="h-4 w-4" />
              <span>Pending activation</span>
            </div>
          )}
        </div>
      </div>

      {/* Payment Prompt */}
      {showPaymentPrompt && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-6 mb-8" data-testid="payment-prompt">
          <div className="flex items-start gap-4">
            <CreditCard className="h-8 w-8 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 mb-2">Complete Your Membership</h3>
              <p className="text-amber-800 text-sm mb-4">
                Your application has been approved! Complete the payment to activate your 
                membership and receive your official certificate.
              </p>
              <Button 
                onClick={handlePayment}
                disabled={paymentLoading}
                className="bg-amber-600 text-white hover:bg-amber-700"
                data-testid="complete-payment-btn"
              >
                {paymentLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Complete Payment
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Certificate Access */}
      {certificate && (
        <div className="bg-white p-6 rounded-sm border border-slate-200 shadow-sm" data-testid="certificate-card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0E285B] mb-2">
                Your Certificate
              </h3>
              <p className="text-slate-600">
                View and download your official UIEHEC membership certificate.
              </p>
            </div>
            <Link to="/member/certificate">
              <Button className="bg-[#F5A623] text-white hover:bg-[#F5A623]/90" data-testid="view-certificate-btn">
                View Certificate
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Application Pending */}
      {application?.status === 'pending' && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-6" data-testid="pending-notice">
          <div className="flex items-start gap-4">
            <Clock className="h-8 w-8 text-blue-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Application Under Review</h3>
              <p className="text-blue-800 text-sm">
                Your membership application is currently being reviewed. 
                We will notify you via email once a decision has been made.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
